/**
 * Address Cleaning Utilities
 *
 * Provides functions to clean and format address strings for display.
 * Handles redundant prefixes and formatting inconsistencies from API data.
 *
 * @module address-cleaner
 */

/**
 * Remove location name prefix from address if it appears at the beginning
 *
 * Prevents redundant display like:
 * "Richland Park Farmers Market, Richland Park Farmers Market, 4711..."
 *
 * @param rawAddress - Full address string from API
 * @param locationName - Location name that might be duplicated in address
 * @returns Cleaned address string with prefix removed
 *
 * @remarks
 * Common in API responses where location name is prepended to address.
 * Case-insensitive matching ensures "Market Name" matches "market name" prefix.
 *
 * @example
 * cleanAddressPrefix(
 *   "Richland Park Farmers Market, 4711 Charlotte Ave",
 *   "Richland Park Farmers Market"
 * )
 * // Returns: "4711 Charlotte Ave"
 */
export function cleanAddressPrefix(
  rawAddress: string,
  locationName: string
): string {
  if (!rawAddress || !locationName) {
    return rawAddress || '';
  }

  const startsWithName = rawAddress
    .toLowerCase()
    .startsWith(locationName.toLowerCase());

  if (!startsWithName) {
    return rawAddress;
  }

  // Remove the name prefix and clean up leading commas/spaces
  return rawAddress.slice(locationName.length).replace(/^[,\s]+/, '');
}
